import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { 
  Routing, 
  RoutingCreateRequest, 
  RoutingUpdateRequest, 
  RoutingSearchFilters,
  RoutingResponse,
  RoutingStatistics
} from '../interfaces/Routing.interfaces';

import {
  Zone,
  ZoneCreateRequest,
  ZoneUpdateRequest,
  ZoneSearchFilters,
  ZoneResponse,
  ZoneStatistics
} from '../interfaces/Zones.interfaces';

import {
  Warehouse,
  WarehouseCreateRequest,
  WarehouseUpdateRequest,
  WarehouseSearchFilters,
  WarehouseResponse,
  WarehouseStatistics
} from '../interfaces/Warehouses.interfaces';

// Import ClientsBrands interface
export interface ClientsBrands {
  clientCode: string;
  clientName?: string;
  brands: Brand[];
}

export interface Brand {
  brandCode: string;
  brandName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RoutingService {
  private readonly baseUrl = '/api'; // Adjust base URL as needed
  
  // BehaviorSubjects for caching and state management
  private routingsSubject = new BehaviorSubject<Routing[]>([]);
  private zonesSubject = new BehaviorSubject<Zone[]>([]);
  private warehousesSubject = new BehaviorSubject<Warehouse[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  
  // Public observables
  public routings$ = this.routingsSubject.asObservable();
  public zones$ = this.zonesSubject.asObservable();
  public warehouses$ = this.warehousesSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  // ===============================
  // ROUTING METHODS
  // ===============================

  /**
   * Get all routings with optional filters and pagination
   */
  getRoutings(clientsBrands: ClientsBrands[], filters?: RoutingSearchFilters): Observable<RoutingResponse> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'GET_ROUTINGS',
      clientsBrands: clientsBrands,
      filters: filters || {}
    };

    return this.http.post<RoutingResponse>(`${this.baseUrl}/routings`, requestBody)
      .pipe(
        tap(response => {
          this.routingsSubject.next(response.content);
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error fetching routings:', error);
          throw error;
        })
      );
  }

  /**
   * Get routing by ID
   */
  getRoutingById(clientsBrands: ClientsBrands[], id: number): Observable<Routing> {
    const requestBody = {
      action: 'GET_ROUTING_BY_ID',
      clientsBrands: clientsBrands,
      routingId: id
    };

    return this.http.post<Routing>(`${this.baseUrl}/routings`, requestBody)
      .pipe(
        catchError(error => {
          console.error('Error fetching routing by ID:', error);
          throw error;
        })
      );
  }

  /**
   * Create new routing
   */
  createRouting(clientsBrands: ClientsBrands[], routing: RoutingCreateRequest): Observable<Routing> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'CREATE_ROUTING',
      clientsBrands: clientsBrands,
      data: routing
    };
    
    return this.http.post<Routing>(`${this.baseUrl}/routings`, requestBody)
      .pipe(
        tap(newRouting => {
          const currentRoutings = this.routingsSubject.value;
          this.routingsSubject.next([...currentRoutings, newRouting]);
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error creating routing:', error);
          throw error;
        })
      );
  }

  /**
   * Update existing routing
   */
  updateRouting(clientsBrands: ClientsBrands[], id: number, routing: RoutingUpdateRequest): Observable<Routing> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'UPDATE_ROUTING',
      clientsBrands: clientsBrands,
      routingId: id,
      data: routing
    };
    
    return this.http.post<Routing>(`${this.baseUrl}/routings`, requestBody)
      .pipe(
        tap(updatedRouting => {
          const currentRoutings = this.routingsSubject.value;
          const index = currentRoutings.findIndex(r => r.routingId === id);
          if (index !== -1) {
            currentRoutings[index] = updatedRouting;
            this.routingsSubject.next([...currentRoutings]);
          }
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error updating routing:', error);
          throw error;
        })
      );
  }

  /**
   * Delete routing by ID
   */
  deleteRouting(clientsBrands: ClientsBrands[], id: number): Observable<void> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'DELETE_ROUTING',
      clientsBrands: clientsBrands,
      routingId: id
    };
    
    return this.http.post<void>(`${this.baseUrl}/routings`, requestBody)
      .pipe(
        tap(() => {
          const currentRoutings = this.routingsSubject.value;
          const filteredRoutings = currentRoutings.filter(r => r.routingId !== id);
          this.routingsSubject.next(filteredRoutings);
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error deleting routing:', error);
          throw error;
        })
      );
  }

  /**
   * Get routing statistics
   */
  getRoutingStatistics(clientsBrands: ClientsBrands[]): Observable<RoutingStatistics> {
    const requestBody = {
      action: 'GET_ROUTING_STATISTICS',
      clientsBrands: clientsBrands
    };
    
    return this.http.post<RoutingStatistics>(`${this.baseUrl}/routings`, requestBody)
      .pipe(
        catchError(error => {
          console.error('Error fetching routing statistics:', error);
          throw error;
        })
      );
  }

  // ===============================
  // ZONE METHODS
  // ===============================

  /**
   * Get all zones with optional filters and pagination
   */
  getZones(clientsBrands: ClientsBrands[], filters?: ZoneSearchFilters): Observable<ZoneResponse> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'GET_ZONES',
      clientsBrands: clientsBrands,
      filters: filters || {}
    };

    return this.http.post<ZoneResponse>(`${this.baseUrl}/zones`, requestBody)
      .pipe(
        tap(response => {
          this.zonesSubject.next(response.content);
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error fetching zones:', error);
          throw error;
        })
      );
  }

  /**
   * Get zone by ID
   */
  getZoneById(clientsBrands: ClientsBrands[], id: number): Observable<Zone> {
    const requestBody = {
      action: 'GET_ZONE_BY_ID',
      clientsBrands: clientsBrands,
      zoneId: id
    };
    
    return this.http.post<Zone>(`${this.baseUrl}/zones`, requestBody)
      .pipe(
        catchError(error => {
          console.error('Error fetching zone by ID:', error);
          throw error;
        })
      );
  }

  /**
   * Create new zone
   */
  createZone(clientsBrands: ClientsBrands[], zone: ZoneCreateRequest): Observable<Zone> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'CREATE_ZONE',
      clientsBrands: clientsBrands,
      data: zone
    };
    
    return this.http.post<Zone>(`${this.baseUrl}/zones`, requestBody)
      .pipe(
        tap(newZone => {
          const currentZones = this.zonesSubject.value;
          this.zonesSubject.next([...currentZones, newZone]);
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error creating zone:', error);
          throw error;
        })
      );
  }

  /**
   * Update existing zone
   */
  updateZone(clientsBrands: ClientsBrands[], id: number, zone: ZoneUpdateRequest): Observable<Zone> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'UPDATE_ZONE',
      clientsBrands: clientsBrands,
      zoneId: id,
      data: zone
    };
    
    return this.http.post<Zone>(`${this.baseUrl}/zones`, requestBody)
      .pipe(
        tap(updatedZone => {
          const currentZones = this.zonesSubject.value;
          const index = currentZones.findIndex(z => z.zoneId === id);
          if (index !== -1) {
            currentZones[index] = updatedZone;
            this.zonesSubject.next([...currentZones]);
          }
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error updating zone:', error);
          throw error;
        })
      );
  }

  /**
   * Delete zone by ID
   */
  deleteZone(clientsBrands: ClientsBrands[], id: number): Observable<void> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'DELETE_ZONE',
      clientsBrands: clientsBrands,
      zoneId: id
    };
    
    return this.http.post<void>(`${this.baseUrl}/zones`, requestBody)
      .pipe(
        tap(() => {
          const currentZones = this.zonesSubject.value;
          const filteredZones = currentZones.filter(z => z.zoneId !== id);
          this.zonesSubject.next(filteredZones);
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error deleting zone:', error);
          throw error;
        })
      );
  }

  /**
   * Get zone statistics
   */
  getZoneStatistics(clientsBrands: ClientsBrands[]): Observable<ZoneStatistics> {
    const requestBody = {
      action: 'GET_ZONE_STATISTICS',
      clientsBrands: clientsBrands
    };
    
    return this.http.post<ZoneStatistics>(`${this.baseUrl}/zones`, requestBody)
      .pipe(
        catchError(error => {
          console.error('Error fetching zone statistics:', error);
          throw error;
        })
      );
  }

  // ===============================
  // WAREHOUSE METHODS
  // ===============================

  /**
   * Get all warehouses with optional filters and pagination
   */
  getWarehouses(clientsBrands: ClientsBrands[], filters?: WarehouseSearchFilters): Observable<WarehouseResponse> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'GET_WAREHOUSES',
      clientsBrands: clientsBrands,
      filters: filters || {}
    };

    return this.http.post<WarehouseResponse>(`${this.baseUrl}/warehouses`, requestBody)
      .pipe(
        tap(response => {
          this.warehousesSubject.next(response.content);
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error fetching warehouses:', error);
          throw error;
        })
      );
  }

  /**
   * Get warehouse by ID
   */
  getWarehouseById(clientsBrands: ClientsBrands[], id: number): Observable<Warehouse> {
    const requestBody = {
      action: 'GET_WAREHOUSE_BY_ID',
      clientsBrands: clientsBrands,
      warehouseId: id
    };
    
    return this.http.post<Warehouse>(`${this.baseUrl}/warehouses`, requestBody)
      .pipe(
        catchError(error => {
          console.error('Error fetching warehouse by ID:', error);
          throw error;
        })
      );
  }

  /**
   * Create new warehouse
   */
  createWarehouse(clientsBrands: ClientsBrands[], warehouse: WarehouseCreateRequest): Observable<Warehouse> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'CREATE_WAREHOUSE',
      clientsBrands: clientsBrands,
      data: warehouse
    };
    
    return this.http.post<Warehouse>(`${this.baseUrl}/warehouses`, requestBody)
      .pipe(
        tap(newWarehouse => {
          const currentWarehouses = this.warehousesSubject.value;
          this.warehousesSubject.next([...currentWarehouses, newWarehouse]);
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error creating warehouse:', error);
          throw error;
        })
      );
  }

  /**
   * Update existing warehouse
   */
  updateWarehouse(clientsBrands: ClientsBrands[], id: number, warehouse: WarehouseUpdateRequest): Observable<Warehouse> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'UPDATE_WAREHOUSE',
      clientsBrands: clientsBrands,
      warehouseId: id,
      data: warehouse
    };
    
    return this.http.post<Warehouse>(`${this.baseUrl}/warehouses`, requestBody)
      .pipe(
        tap(updatedWarehouse => {
          const currentWarehouses = this.warehousesSubject.value;
          const index = currentWarehouses.findIndex(w => w.warehouseId === id);
          if (index !== -1) {
            currentWarehouses[index] = updatedWarehouse;
            this.warehousesSubject.next([...currentWarehouses]);
          }
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error updating warehouse:', error);
          throw error;
        })
      );
  }

  /**
   * Delete warehouse by ID
   */
  deleteWarehouse(clientsBrands: ClientsBrands[], id: number): Observable<void> {
    this.loadingSubject.next(true);
    
    const requestBody = {
      action: 'DELETE_WAREHOUSE',
      clientsBrands: clientsBrands,
      warehouseId: id
    };
    
    return this.http.post<void>(`${this.baseUrl}/warehouses`, requestBody)
      .pipe(
        tap(() => {
          const currentWarehouses = this.warehousesSubject.value;
          const filteredWarehouses = currentWarehouses.filter(w => w.warehouseId !== id);
          this.warehousesSubject.next(filteredWarehouses);
          this.loadingSubject.next(false);
        }),
        catchError(error => {
          this.loadingSubject.next(false);
          console.error('Error deleting warehouse:', error);
          throw error;
        })
      );
  }

  /**
   * Get warehouse statistics
   */
  getWarehouseStatistics(clientsBrands: ClientsBrands[]): Observable<WarehouseStatistics> {
    const requestBody = {
      action: 'GET_WAREHOUSE_STATISTICS',
      clientsBrands: clientsBrands
    };
    
    return this.http.post<WarehouseStatistics>(`${this.baseUrl}/warehouses`, requestBody)
      .pipe(
        catchError(error => {
          console.error('Error fetching warehouse statistics:', error);
          throw error;
        })
      );
  }

  // ===============================
  // UTILITY METHODS
  // ===============================

  /**
   * Export routings to Excel
   */
  exportRoutingsToExcel(clientsBrands: ClientsBrands[], filters?: RoutingSearchFilters): Observable<Blob> {
    const requestBody = {
      action: 'EXPORT_ROUTINGS_TO_EXCEL',
      clientsBrands: clientsBrands,
      filters: filters || {}
    };

    return this.http.post(`${this.baseUrl}/routings`, requestBody, {
      responseType: 'blob'
    }).pipe(
      catchError(error => {
        console.error('Error exporting routings to Excel:', error);
        throw error;
      })
    );
  }

  /**
   * Export zones to Excel
   */
  exportZonesToExcel(clientsBrands: ClientsBrands[], filters?: ZoneSearchFilters): Observable<Blob> {
    const requestBody = {
      action: 'EXPORT_ZONES_TO_EXCEL',
      clientsBrands: clientsBrands,
      filters: filters || {}
    };

    return this.http.post(`${this.baseUrl}/zones`, requestBody, {
      responseType: 'blob'
    }).pipe(
      catchError(error => {
        console.error('Error exporting zones to Excel:', error);
        throw error;
      })
    );
  }

  /**
   * Export warehouses to Excel
   */
  exportWarehousesToExcel(clientsBrands: ClientsBrands[], filters?: WarehouseSearchFilters): Observable<Blob> {
    const requestBody = {
      action: 'EXPORT_WAREHOUSES_TO_EXCEL',
      clientsBrands: clientsBrands,
      filters: filters || {}
    };

    return this.http.post(`${this.baseUrl}/warehouses`, requestBody, {
      responseType: 'blob'
    }).pipe(
      catchError(error => {
        console.error('Error exporting warehouses to Excel:', error);
        throw error;
      })
    );
  }

  /**
   * Clear cached data
   */
  clearCache(): void {
    this.routingsSubject.next([]);
    this.zonesSubject.next([]);
    this.warehousesSubject.next([]);
  }

  /**
   * Get current cached routings
   */
  getCurrentRoutings(): Routing[] {
    return this.routingsSubject.value;
  }

  /**
   * Get current cached zones
   */
  getCurrentZones(): Zone[] {
    return this.zonesSubject.value;
  }

  /**
   * Get current cached warehouses
   */
  getCurrentWarehouses(): Warehouse[] {
    return this.warehousesSubject.value;
  }
}
